import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import CustomerContext from '../../../../context/customer-context';
import {
  deleteCategorisation,
  getCategorisationForCustomer,
  saveCategorisation,
} from '../../../../services/mifidCategorisation/mifidCategorisationService';
import APIError from '../../../../services/APIError';
import CustomerMifidCategory, {
  MifidCategory,
  MifidProduct,
} from '../../../../models/CustomerMifidCategory';
import styles from '../MifidRelatedData.module.css';
import Select from '../../../common/Select/Select';

function Category() {
  const { t } = useTranslation();
  const customerCtx = useContext(CustomerContext);
  const getInitMifidCategory = (product: MifidProduct) => ({
    person: customerCtx.customer,
    product,
  });
  const [isEdit, setIsEdit] = useState(false);
  const [categories, setCategories] = useState<CustomerMifidCategory[]>([]);
  const [general, setGeneral] = useState<CustomerMifidCategory>(
    getInitMifidCategory('GENERAL'),
  );
  const [bond, setBonds] = useState<CustomerMifidCategory>(
    getInitMifidCategory('BONDS'),
  );
  const [shares, setShares] = useState<CustomerMifidCategory>(
    getInitMifidCategory('SHARES'),
  );
  const [fundsEtf, setFundsEtf] = useState<CustomerMifidCategory>(
    getInitMifidCategory('FUNDS_ETF'),
  );
  const [complexFund, setComplexFund] = useState<CustomerMifidCategory>(
    getInitMifidCategory('COMPLEX_FUND'),
  );
  const [otherComplex, setOtherComplex] = useState<CustomerMifidCategory>(
    getInitMifidCategory('COMPLEX_OTHER'),
  );
  const [showExceptions, setShowExceptions] = useState(false);

  const getCategories = async () => {
    try {
      const result = await getCategorisationForCustomer({ id: customerCtx.customer.id });
      setCategories(result);
    } catch (err) {
      if (err instanceof APIError) {
        console.log(err.errorBody);
      } else {
        console.log(err);
      }
    }
  };

  const findCategory = (product: MifidProduct) => categories.find(
    element => element.product === product,
  );

  useEffect(() => {
    getCategories();
  }, [customerCtx.customer.id]);

  useEffect(() => {
    setGeneral(findCategory('GENERAL') || getInitMifidCategory('GENERAL'));
    setBonds(findCategory('BONDS') || getInitMifidCategory('BONDS'));
    setShares(findCategory('SHARES') || getInitMifidCategory('SHARES'));
    setFundsEtf(findCategory('FUNDS_ETF') || getInitMifidCategory('FUNDS_ETF'));
    setComplexFund(findCategory('COMPLEX_FUND') || getInitMifidCategory('COMPLEX_FUND'));
    setOtherComplex(findCategory('COMPLEX_OTHER') || getInitMifidCategory('COMPLEX_OTHER'));
    setShowExceptions(categories.length > 1);
  }, [categories]);

  const allCategories = [
    { value: 'RETAIL' },
    { value: 'PROFESSIONAL' },
    { value: 'ELIGIBLE_COUNTERPARTY' },
  ];

  const allExceptionsCategories = [
    { value: 'N/A' },
    { value: 'RETAIL' },
    { value: 'PROFESSIONAL' },
    { value: 'ELIGIBLE_COUNTERPARTY' },
  ];

  const saveCategory = async (category: CustomerMifidCategory) => {
    try {
      await saveCategorisation({ ...category });
    } catch (err) {
      if (err instanceof APIError) {
        console.log(err.errorBody);
      } else {
        console.log(err);
      }
    }
  };

  const deleteCategory = async (category: CustomerMifidCategory) => {
    try {
      await deleteCategorisation({ categorisationId: category.id! });
    } catch (err) {
      if (err instanceof APIError) {
        console.log(err.errorBody);
      } else {
        console.log(err);
      }
    }
  };

  const saveOrDeleteException = (exception: CustomerMifidCategory) => {
    if (exception.mifidCategory) {
      if (exception.mifidCategory === 'N/A' || !showExceptions) {
        deleteCategory(exception);
      } else {
        saveCategory(exception);
      }
    }
  };

  const showExceptionsView = () => bond.mifidCategory ||
    shares.mifidCategory ||
    fundsEtf.mifidCategory ||
    complexFund.mifidCategory ||
    otherComplex.mifidCategory;

  const handleSaveButton = () => {
    if (general.mifidCategory) {
      saveCategory(general);
    } else {
      saveCategory({ ...general, mifidCategory: 'RETAIL' });
    }
    saveOrDeleteException(bond);
    saveOrDeleteException(shares);
    saveOrDeleteException(fundsEtf);
    saveOrDeleteException(complexFund);
    saveOrDeleteException(otherComplex);
    setIsEdit(false);
    setTimeout(() => {
      getCategories();
    }, 500);
  };

  const handleCancelButton = () => {
    setIsEdit(false);
    setGeneral(findCategory('GENERAL') || getInitMifidCategory('GENERAL'));
    setBonds(findCategory('BONDS') || getInitMifidCategory('BONDS'));
    setShares(findCategory('SHARES') || getInitMifidCategory('SHARES'));
    setFundsEtf(findCategory('FUNDS_ETF') || getInitMifidCategory('FUNDS_ETF'));
    setComplexFund(findCategory('COMPLEX_FUND') || getInitMifidCategory('COMPLEX_FUND'));
    setOtherComplex(findCategory('COMPLEX_OTHER') || getInitMifidCategory('COMPLEX_OTHER'));
  };

  const updateCategories = (product: MifidProduct, mifidCategory: MifidCategory) => {
    switch (product) {
      case 'GENERAL':
        setGeneral({ ...general, mifidCategory });
        break;
      case 'BONDS':
        setBonds({ ...bond, mifidCategory });
        break;
      case 'SHARES':
        setShares({ ...shares, mifidCategory });
        break;
      case 'FUNDS_ETF':
        setFundsEtf({ ...fundsEtf, mifidCategory });
        break;
      case 'COMPLEX_FUND':
        setComplexFund({ ...complexFund, mifidCategory });
        break;
      case 'COMPLEX_OTHER':
        setOtherComplex({ ...otherComplex, mifidCategory });
        break;
      default:
        console.log('Update category failed');
    }
  };

  return (
    <>
      <div className={styles.buttonContainer}>
        {!isEdit && (
          <button
            type='button'
            className={styles.button}
            onClick={() => setIsEdit(true)}
          >
            {t('common.edit')}
          </button>
        )}
        {isEdit && (
          <div>
            <button
              type='button'
              className={styles.button}
              onClick={() => handleSaveButton()}
            >
              {t('common.save')}
            </button>
            <button
              type='button'
              className={styles.button}
              onClick={() => handleCancelButton()}
            >
              {t('common.cancel')}
            </button>
          </div>
        )}
      </div>
      <div className={classNames(styles.container, isEdit && styles.editContainer)}>
        <div>
          <div className={styles.label}>
            {t('customer.dashboard.mifid.category')}
          </div>
          {!isEdit && (
            <>
              <div>{general.mifidCategory}</div>
              {showExceptionsView() && (
                <div className={styles.instruments}>
                  <div className={styles.instrument}>
                    <div className={styles.label}>
                      {t('customer.dashboard.mifid.bonds')}
                    </div>
                    <div>{bond.mifidCategory}</div>
                  </div>
                  <div className={styles.instrument}>
                    <div className={styles.label}>
                      {t('customer.dashboard.mifid.shares')}
                    </div>
                    <div>{shares.mifidCategory}</div>
                  </div>
                  <div className={styles.instrument}>
                    <div className={styles.label}>
                      {t('customer.dashboard.mifid.fundsEtf')}
                    </div>
                    <div>{fundsEtf.mifidCategory}</div>
                  </div>
                  <div className={styles.instrument}>
                    <div className={styles.label}>
                      {t('customer.dashboard.mifid.complexFund')}
                    </div>
                    <div>{complexFund.mifidCategory}</div>
                  </div>
                  <div className={styles.instrument}>
                    <div className={styles.label}>
                      {t('customer.dashboard.mifid.complexOther')}
                    </div>
                    <div>{otherComplex.mifidCategory}</div>
                  </div>
                </div>
              )}
            </>
          )}
          {isEdit && (
            <div className={styles.inputFields}>
              <div className={styles.general}>
                <Select
                  id='general'
                  options={customerCtx.customer.personType === 'LEGAL'
                    ? allCategories
                    : allCategories.slice(0, -1)}
                  label={t('customer.dashboard.mifid.general')}
                  required
                  value={general.mifidCategory || 'RETAIL'}
                  onUpdate={(value) => updateCategories('GENERAL', value)}
                />
                <div className={styles.checkbox}>
                  <input
                    type='checkbox'
                    checked={showExceptions}
                    onChange={() => setShowExceptions(prevState => !prevState)}
                  />
                  <span>{t('customer.dashboard.mifid.exceptions')}</span>
                </div>
              </div>
              {showExceptions && (
                <div className={styles.instruments}>
                  <div className={styles.instrument}>
                    <Select
                      id='bonds'
                      options={customerCtx.customer.personType === 'LEGAL'
                        ? allExceptionsCategories
                        : allExceptionsCategories.slice(0, -1)
                      }
                      label={t('customer.dashboard.mifid.bonds')}
                      required
                      value={bond.mifidCategory || 'N/A'}
                      width='285px'
                      onUpdate={(value) => updateCategories('BONDS', value)}
                    />
                  </div>
                  <div className={styles.instrument}>
                    <Select
                      id='shares'
                      options={customerCtx.customer.personType === 'LEGAL'
                        ? allExceptionsCategories
                        : allExceptionsCategories.slice(0, -1)
                      }
                      label={t('customer.dashboard.mifid.shares')}
                      required
                      value={shares.mifidCategory || 'N/A'}
                      width='285px'
                      onUpdate={(value) => updateCategories('SHARES', value)}
                    />
                  </div>
                  <div className={styles.instrument}>
                    <Select
                      id='fundEtf'
                      options={customerCtx.customer.personType === 'LEGAL'
                        ? allExceptionsCategories
                        : allExceptionsCategories.slice(0, -1)
                      }
                      label={t('customer.dashboard.mifid.fundsEtf')}
                      required
                      value={fundsEtf.mifidCategory || 'N/A'}
                      width='285px'
                      onUpdate={(value) => updateCategories('FUNDS_ETF', value)}
                    />
                  </div>
                  <div className={styles.instrument}>
                    <Select
                      id='complex_funds'
                      options={customerCtx.customer.personType === 'LEGAL'
                        ? allExceptionsCategories
                        : allExceptionsCategories.slice(0, -1)
                      }
                      label={t('customer.dashboard.mifid.complexFund')}
                      isLongLabel
                      required
                      value={complexFund.mifidCategory || 'N/A'}
                      width='285px'
                      onUpdate={(value) => updateCategories('COMPLEX_FUND', value)}
                    />
                  </div>
                  <div className={styles.instrument}>
                    <Select
                      id='complexOther'
                      options={customerCtx.customer.personType === 'LEGAL'
                        ? allExceptionsCategories
                        : allExceptionsCategories.slice(0, -1)
                      }
                      label={t('customer.dashboard.mifid.complexOther')}
                      isLongLabel
                      required
                      value={otherComplex.mifidCategory || 'N/A'}
                      width='285px'
                      onUpdate={(value) => updateCategories('COMPLEX_OTHER', value)}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Category;
